import { maritalStatus } from './Patient.utils.ValueSets';

/* A way to access environment variables. */
/* This is a way to access environment variables in a React app. */
const { REACT_APP_DOMAIN } = process.env;

/**
 * It takes a set of values and returns a Patient resource in FHIR format
 * @param values - The values from the form.
 * @returns A patient object
 */
export function mPatient(values) {
  const patientData = {
    resourceType: 'Patient',
    name: [
      {
        family: [values.name_family],
        given: [values.name_given],
        text : `${values.name_given} ${values.name_family}`,
        resourceType: 'HumanName',
      },
    ],
    active: true,
    deceasedBoolean: values.deceasedBoolean,
  };

  if (values.name_suffix !== '') {
    patientData.name[0].suffix = [values.name_suffix];
      if (values['patient-mothersMaidenName'] !== '') {
        patientData.name[0].text = `${values.name_given} ${values['patient-mothersMaidenName']} ${values.name_family} ${values.name_suffix}`;
      } else {
        patientData.name[0].text = `${values.name_given} ${values.name_family} ${values.name_suffix}`;
      }
  }

  if (values.birthDate !== '') {
    patientData.birthDate = values.birthDate;
  }

  if (values.gender !== '') {
    patientData.gender = values.gender;
  }

  if (
    values.address_line !== '' ||
    values.address_city !== '' ||
    values.address_postalCode !== '' ||
    values.address_country !== ''
  ) {
    patientData.address = [{}];
    if (values.address_line !== '') {
      patientData.address[0].line = values.address_line;
    }
    if (values.address_city !== '') {
      patientData.address[0].city = values.address_city;
    }
    if (values.address_postalCode !== '') {
      patientData.address[0].postalCode = values.address_postalCode;
    }
    if (values.address_country !== '') {
      patientData.address[0].country = values.address_country;
    }
  }

  if (values.telecom_phone_value !== '' || values.telecom_email_value !== '') {
    patientData.telecom = [];
    if (values.telecom_phone_value !== '') {
      patientData.telecom.push({
        system: 'phone',
        value: values.telecom_phone_value,
      });
    }
    if (values.telecom_email_value !== '') {
      patientData.telecom.push({
        system: 'email',
        value: values.telecom_email_value,
      });
    }
  }

  if (values.maritalStatus_code !== '') {
    patientData.maritalStatus = {
      coding: maritalStatus.filter((val) => val.code === values.maritalStatus_code),
    };
  }

  if (
    values.contact_name_text !== '' ||
    values.contact_relationship_text !== '' ||
    values.contact_telecom_value !== ''
  ) {
    patientData.contact = [{}];
    if (values.contact_name_text !== '') {
      patientData.contact[0].name = {
        text: values.contact_name_text,
        resourceType: 'HumanName',
      };
    }
    if (values.contact_relationship_text !== '') {
      patientData.contact[0].relationship = {
        text: values.contact_relationship_text,
      };
    }
    if (values.contact_telecom_value !== '') {
      patientData.contact[0].telecom = [
        {
          system: 'phone',
          value: values.contact_telecom_value,
        },
      ];
    }
  }

  if (values['philhealthNo-id'] !== '' || values['SSSNo-id'] !== '' || values['GSISNo-id'] !== '') {
    patientData.identifier = [];

    if (values['philhealthNo-id'] !== '') {
      patientData.identifier.push({
        system: `${REACT_APP_DOMAIN}/NamingSystem/philhealthNo-id`,
        value: values['philhealthNo-id'],
      });
    }

    if (values['SSSNo-id'] !== '') {
      patientData.identifier.push({
        system: `${REACT_APP_DOMAIN}/NamingSystem/SSSNo-id`,
        value: values['SSSNo-id'],
      });
    }

    if (values['GSISNo-id'] !== '') {
      patientData.identifier.push({
        system: `${REACT_APP_DOMAIN}/NamingSystem/GSISNo-id`,
        value: values['GSISNo-id'],
      });
    }
  }

  if (values['patient-nationality'] !== '') {
    if (!Array.isArray(patientData.extension)) {
      patientData.extension = [];
    }
    patientData.extension.push({
      url: 'http://hl7.org/fhir/StructureDefinition/patient-nationality',
      valueString: values['patient-nationality'],
    });
  }

  if (values['patient-mothersMaidenName'] !== '') {
    if (!Array.isArray(patientData.extension)) {
      patientData.extension = [];
    }
    patientData.extension.push({
      url: 'http://hl7.org/fhir/StructureDefinition/patient-mothersMaidenName',
      valueString: values['patient-mothersMaidenName'],
    });
  }

  if (values['patient-birthPlace'] !== '') {
    if (!Array.isArray(patientData.extension)) {
      patientData.extension = [];
    }
    patientData.extension.push({
      url: 'http://hl7.org/fhir/StructureDefinition/patient-birthPlace',
      valueAddress: {
        text: values['patient-birthPlace'],
      },
    });
  }

  if (values['patient-hmo'] !== '') {
    if (!Array.isArray(patientData.extension)) {
      patientData.extension = [];
    }
    patientData.extension.push({
      url: `${REACT_APP_DOMAIN}/StructureDefinition/patient-hmo`,
      valueString: values['patient-hmo'],
    });
  }

  if (values['patient-pregnancyStatus'] !== '') {
    if (!Array.isArray(patientData.extension)) {
      patientData.extension = [];
    }
    patientData.extension.push({
      url: `${REACT_APP_DOMAIN}/StructureDefinition/patient-pregnancyStatus`,
      valueBoolean: values['patient-pregnancyStatus'],
    });
  }

  if (values['patient-remarks'] !== '') {
    if (!Array.isArray(patientData.extension)) {
      patientData.extension = [];
    }
    patientData.extension.push({
      url: `${REACT_APP_DOMAIN}/StructureDefinition/patient-remarks`,
      valueString: values['patient-remarks'],
    });
  }

  const lifelineConfig = [
    'patient-lifeline-config-ecg_st_msec',
    'patient-lifeline-config-heartrate_upper_bpm',
    'patient-lifeline-config-heartrate_lower_bpm',
    'patient-lifeline-config-pulserate_upper_bpm',
    'patient-lifeline-config-pulserate_lower_bpm',
    'patient-lifeline-config-oxygen_upper_saturation',
    'patient-lifeline-config-oxygen_lower_saturation',
    'patient-lifeline-config-respiratory_upper_rpm',
    'patient-lifeline-config-respiratory_lower_rpm',
    'patient-lifeline-config-bp_systolic_upper',
    'patient-lifeline-config-bp_systolic_lower',
    'patient-lifeline-config-bp_diastolic_upper',
    'patient-lifeline-config-bp_diastolic_lower',
    'patient-lifeline-config-time_frame',
    'patient-lifeline-config-temperature_upper',
    'patient-lifeline-config-temperature_lower',
  ];

  let hasConfig = false;
  lifelineConfig.forEach((config) => {
    if (values[config] !== '') {
      hasConfig = true;
    }
  });

  if (hasConfig) {
    const configExtension = [];

    lifelineConfig.forEach((config) => {
      if (values[config] !== '') {
        configExtension.push({
          url: config.split('-')[3],
          valueString: values[config],
        });
      }
    });

    if (!Array.isArray(patientData.extension)) {
      patientData.extension = [];
    }

    patientData.extension.push({
      url: `${REACT_APP_DOMAIN}/StructureDefinition/patient-lifeline-config`,
      extension: configExtension,
    });
  }

  return patientData;
}
